/**
 * Поиск и свободный ввод в выпадающем списке: строка поиска,
 * проверка совпадения, добавление произвольных значений и обработка
 * вставки из буфера обмена.
 */
export const search = {
  props: {
    /**
     * Признак поля с поиском. Добавляет компоненту свое поле ввода
     * и модификатор класса корневого элемента.
     *
     * @type {boolean}
     * @default false
     */
    autocomplete: {
      type: Boolean,
      default: false
    },
    /**
     * Признак поля с произвольным вводом. Добавляет компоненту
     * свое поле ввода и разрешает добавлять значения, которых нет в
     * списке.
     *
     * @type {boolean}
     * @default false
     */
    combobox: {
      type: Boolean,
      default: false
    },

    /**
     * Признак свободного ввода. Разрешает добавлять значение из
     * строки поиска по клавишам-разделителям.
     *
     * @type {boolean}
     * @default false
     */
    freeInput: {
      type: Boolean,
      default: false
    },
    /**
     * Клавиши, по которым строка поиска добавляется к выбранным
     * значениям. Задаются кодом клавиши или ее названием.
     *
     * @type {Array<number|string>|string|number}
     * @default [ 9, 13 ]
     */
    freeInputTriggers: {
      type: [ Array, String, Number ],
      default: () => {
        return [ 9, 13 ]
      }
    },

    /**
     * Признак добавления строки поиска при нажатии вне
     * компонента. Действует только при свободном вводе.
     *
     * @type {boolean}
     */
    setOnBlur: Boolean,
    /**
     * Признак очистки строки поиска при нажатии по полю.
     * Действует только при combobox.
     *
     * @type {boolean}
     */
    clearOnFocus: Boolean,

    /**
     * Проверка совпадения элемента со строкой поиска. Если задана,
     * полностью заменяет сравнение по заголовку и значению.
     *
     * @type {Function}
     * @default undefined
     */
    searchValidation: {
      type: Function,
      default: undefined
    },

    /**
     * Признак обработки вставки из буфера обмена. Позволяет
     * добавить сразу несколько значений из вставленного текста.
     *
     * @type {boolean}
     */
    paste: Boolean,
    /**
     * Разбор вставленного текста на значения. По умолчанию отдает
     * массив из одного значения - всего вставленного текста.
     *
     * @type {Function}
     */
    pasteFilter: {
      type: Function,
      default: v => {
        return [ v ]
      }
    }
  },

  /**
   * Локальное состояние поиска.
   * Поле search хранит строку поиска.
   *
   * @returns {{ search: string }} Локальное состояние.
   */
  data() {
    return {
      search: ''
    }
  },

  computed: {
    /**
     * Отдает признак свободного ввода.
     * Свободный ввод включен, если freeInput или combobox.
     *
     * @returns {boolean} Признак свободного ввода.
     */
    isFreeInput() {
      return this.freeInput || this.combobox
    },

    /**
     * Отдает признак наличия своего поля ввода.
     * Поле ввода выводится, если autocomplete или combobox.
     *
     * @returns {boolean} Признак наличия поля ввода.
     */
    hasInput() {
      return this.autocomplete || this.combobox
    }
  },

  methods: {
    /**
     * Сравнивает значение со строкой поиска без учета регистра.
     * Совпадением считается равенство значений или вхождение
     * строки поиска в значение.
     *
     * @param {*} value Проверяемое значение.
     * @param {*} search Строка поиска.
     * @returns {boolean} Признак совпадения.
     */
    _compareStrings(value, search) {
      const v = ('' + value).toLowerCase()
      const s = ('' + search).toLowerCase()

      return this._compareValues(v, s) || v.includes(s)
    },
    /**
     * Проверяет, подходит ли элемент под строку поиска.
     * Совпадение ищется по значению и по заголовку. Если задано
     * свойство searchValidation, отдается его результат.
     *
     * @param {string} title Заголовок элемента.
     * @param {*} value Значение элемента.
     * @param {object|*} item Исходный элемент.
     * @returns {boolean} Признак того, что элемент подходит.
     */
    _checkSearchValidity(title, value, item) {
      const checkValue = this._compareStrings(value, this.search)
      const checkTitle = this._compareStrings(title, this.search)

      let result = checkValue || checkTitle
      if (typeof this.searchValidation === 'function') {
        result = this.searchValidation(this.search, item)
      }

      return result
    },

    /**
     * Очищает строку поиска после перерисовки.
     */
    _clearSearchValue() {
      this.$nextTick(() => {
        this.search = undefined
      })
    },

    /**
     * Обрабатывает ввод в поле поиска.
     * Если значение совпадает с текущей строкой поиска, ничего не
     * происходит: так поле ввода сообщает свое начальное значение при
     * монтировании. Отсутствующее значение считается пустой строкой.
     * Иначе строка поиска обновляется, наружу
     * отправляется событие search с введенным значением, фокус
     * возвращается в поле ввода.
     *
     * @param {string} event Введенное значение.
     */
    _inputHandler(event) {
      if ((event ?? '') === (this.search ?? '')) {
        return
      }

      this.search = event
      this.$emit('search', event)
      this._focus()
    },
    /**
     * Обрабатывает вставку из буфера обмена.
     * Если нет paste, ничего не происходит. Иначе вставка по
     * умолчанию отменяется, текст разбирается свойством
     * pasteFilter, результат приводится к массиву и передается в
     * выбранные значения.
     *
     * @param {ClipboardEvent} event Событие вставки.
     */
    _pasteHandler(event) {
      if (this.paste) {
        event.preventDefault()

        const clipboardData = event.clipboardData || window.clipboardData

        let text = ''
        if (clipboardData && typeof clipboardData.getData === 'function') {
          text = clipboardData.getData('text')
        }
        let output = this.pasteFilter(text)
        if (!Array.isArray(output)) {
          output = [ output ]
        }
        this.proxy = [ ...output ]
      }
    }
  }
}
